import { SafeUrl } from '@angular/platform-browser';
import { ECurrencyCode, EInstrumentCategory, EInstrumentTarget, EInvestmentFrequency, EInvestmentType, EManagementType, EsgTypes, IImageUploadProps, IncomeTypes, InstrumentTargets, InstrumentTypes } from './common.model';
import { IClientCollaboratorOut } from './user.model';
import { IAllInvestmentsListItem, ITransaction } from './transactions.model';
import dayjs from 'dayjs';
import { IBackcastingChartOut, IHistoryChartOut, IInstrumentChartOut, IInvestmentModelChartOut } from './charts.model';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

export interface IPortfolioFeeConfigsInfo {
  portfolioId: number;
  feeConfigs: IPortfolioFeeConfigs;
}

export interface IProductsState {
  portfolios: IPortfolioProduct[] | IPortfolioOverview[]; // todo rename
  allInvestments: IAllInvestmentsListItem[];
  thematicStrategies: IApiThematicStrategy[];
  draftInvestments: IDraftPortfolioOut[];
  selectedPortfolio: IPortfolioProduct | IPortfolioOverview | IPortfolioDetail | null;
  selectedDraftPortfolio: IPortfolioProduct | null;
  portfoliosTotal: number | null;
  portfolioFeeConfigs: IPortfolioFeeConfigsInfo | null;
  portfolioHistoryChartData: IHistoryChartOut | null;
  modelPortfolio: IModelPortfolioOut | null;
  modelPortfolioByRiskScore: IModelPortfolioByRiskScore | null;
  investmentFutureDevelopmentChart: IInvestmentModelChartOut | null;
  backcastingChart: IBackcastingChartOut | null;
  instrumentChart: IInstrumentChartOut | null;
  instrumentRatios: IPortfolioInstrumentRatios | null;
  entryFeeToDisplay: IEntryFeeCalculationWithAllRatiosOut | null;
  newInvestmentProgress: INewInvestmentProgress | null;
  editInvestmentProgress: IEditInvestmentProgress | null;
  availableInstruments: IInstrumentDetailFullOut[];
  publicInstruments: IInstrumentDetailFullOut[];
  cashInstruments: IBaseInstrument[];
  instrumentRegions: string[];
  publicInstrumentRegions: string[];
  strategyLimits: IStrategyLimitOut | null;
  selectedModelStrategy: IModelProductWithInstrumentsOut | null;
  depositInfo: IPortfolioDepositOut | null;
  selectedPortfolioInstrumentRemovals: IInvestmentInstrumentRemoval[];
}

export interface IMinioImage {
  exists: boolean;
  url: string;
}

export interface IPortfolioOverview {
  portfolioId: number;
  portfolioType: EManagementType;
  name: string;
  balance: number;
  actualYield: number;
  twrYield: number;
  dip: boolean;
  currency: ECurrencyCode;
  investmentGoal: EInvestmentGoal | null;
  image: IMinioImage;
  efektika: boolean;
  scheduledInstrumentRemovalDate: string | null;
  exclusive: boolean;
  exclusiveThreshold: IExclusiveThreshold;
  isInLiquidation?: boolean,
}
export interface IPortfolioProduct extends IPortfolioOverview {
  sumOfDeposits: number
  sumOfWithdrawals: number
  initialDeposit: number
  regularDeposit: number
  canRemovePortfolio: boolean
  totalYearsToInvest: number
  investmentFrequency: EInvestmentFrequency
  targetYear: number
  expectedValueInTargetYear: number
  riskScore?: number // only portfolio
  minWithdrawalAmount: number
  isLiquidationScheduled: boolean
  isInLiquidation: boolean
  strategyRebalance: boolean
  weeksBetweenInvestments: number, // set when fetched
  // DRAFT
  draftPortfolioId: number,
  autoExpiryTime?: string,
  consultantPhoto?: SafeUrl,
  collaborator?: IClientCollaboratorOut,
  messageForClient?: string,
  isDraft: boolean,
  modelPortfolioInstruments?: IInvestmentModelInstrument[],
}

export interface IInvestmentModelInstrument {
  instrument: IInstrumentDetailFullOut,
  weight: number,
}

export interface IBaseInstrument {
  [index: string]: unknown,
  instrumentId: number,
  isin: string,
  currencyCode: ECurrencyCode,
  cash?: boolean,
}

export interface IInstrumentDetailFullOut extends IBaseInstrument {
  [index: string]: unknown,
  name: string,
  humanName: string,
  instrumentCategory: EInstrumentCategory,
  description: string,
  ter: number,
  web: string,
  riskScore: number,
  yield1Y: number,
  yield3Y: number,
  yield5Y: number,
  instrumentTarget: InstrumentTargets,
  instrumentType: InstrumentTypes,
  instrumentIncomeType: IncomeTypes,
  esg: EsgTypes,
  regionalOrientations: string[]
  isInEnglish: boolean,
  rebalancedMonthly: boolean,
  prices1: (number | null)[],
  prices3: (number | null)[],
  prices5: (number | null)[],
  visibleToCollaborator: boolean,
  willBeRemoved: boolean,
  insertable: boolean;
  okSmartBond: boolean,
  iconUrl: string;
  price: number;
}

export interface IStrategyLimitOut {
  minCashThreshold: number,
  minInstrumentThreshold: number,
  minCashRebalancedMonthlyThreshold: number,
}

export interface IApiSummarySum {
  sumOfDeposits: number,
  sumOfWithdrawals: number,
  actualYield: number,
  nextRebalancingDate: string,
  portfolios: IPortfolioProduct[],
  totalBalance: number,
}

export interface IPortfolioOverviewDisplayed {
  portfolioId: number;
  portfolioType: EManagementType;
  img: string | null;
  portfolioName: string;
  isDip: boolean;
  value: number;
  valueChangeAbsolute: number;
  valueChangePercentage: number;
  // fulfilledPercentage: number;
  portfolioGoal: string | null;
  currency: ECurrencyCode;
  isEfektika: boolean;
  scheduledInstrumentRemovalDate: string | null;
  containesWillBeRemovedInstrument: boolean;
  exclusive: boolean,
  exclusiveThreshold?: IExclusiveThreshold,
  isInLiquidation: boolean,
}

export enum EExclusiveStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  LIQUIDATION = 'LIQUIDATION',
  COMPLETED = 'COMPLETED',
}

export interface IPortfolioOverviewPage {
  content: IPortfolioOverview[];
  totalCount: number;
}

export interface IDraftsResponse {
  draftPortfolios: IDraftPortfolioOut[];
  aml: boolean;
}

export interface IPortfolioDetail {
  summary: IPortfolioDetailSummary;
  creationDate: string;
  totalYearsToInvest: number;
  initialDeposit: number;
  regularDeposit: number;
  variableSymbol: string;
  missingRegularDeposit: number;
  investmentGoal: EInvestmentGoal | null;
  portfolioId: number;
  name: string;
  portfolioType: EManagementType;
  currency: ECurrencyCode;
  targetYear: number;
  expectedValueInTargetYear: number;
  riskScore: number | null;
  minWithdrawalAmount: number;
  dip: boolean;
  isLiquidationScheduled: boolean;
  isInLiquidation: boolean;
  strategyRebalance: boolean;
  removable: boolean;
  image: IMinioImage;
  depositFrequency: EInvestmentFrequency;
  frameworkContractId: number;
  twrYield: number;
  efektika: boolean;
  exclusive: boolean;
  exclusiveThreshold: IExclusiveThreshold | null;
  scheduledInstrumentRemovalDate: string | null;
  activePortfoliosCount: number;
}

export interface IExclusiveThreshold {
  thresholdAmount: number;
  thresholdReachedDate?: string | null;
  daysRemaining?: number;
  remainingAmount?: number;
}

export interface IInvestmentInstrumentRemoval {
  proposalId: number;
  status: IInvestmentInstrumentRemovalStatus;
  type: IInvestmentInstrumentRemovalType;
  validTo: string;
  canAccept: boolean;
  cannotAcceptReason: EnvestmentInstrumentRemovalCannotAcceptReason[] | null;
  feeChangeConfirmationRequired: boolean;
  items: IRemovedInstrument[];
}

export enum EnvestmentInstrumentRemovalCannotAcceptReason {
  MISSING_ASSET_CLASS = 'MISSING_ASSET_CLASS',
  MISSING_ENGLISH_CONSENT = 'MISSING_ENGLISH_CONSENT',
}
export interface IRemovedInstrument {
  itemId: number;
  itemType: IRemovedInstrumentType;
  status: IRemovedInstrumentStatus;
  removalIsin: string;
  removalInstrumentHumanName: string;
  removalInstrumentName: string;
  newIsin: string | null;
  newInstrumentHumanName: string | null;
  newInstrumentName: string | null;
}

export enum IInvestmentInstrumentRemovalStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
  RESOLVED_BY_CLIENT = 'RESOLVED_BY_CLIENT'
}

export enum IInvestmentInstrumentRemovalType {
  INSTRUMENT_REMOVAL = 'INSTRUMENT_REMOVAL',
  INSTRUMENT_REPLACEMENT = 'INSTRUMENT_REPLACEMENT',
}

export enum IRemovedInstrumentType {
  REPLACE_INSTRUMENT = 'REPLACE_INSTRUMENT',
  REMOVE_INSTRUMENT = 'REMOVE_INSTRUMENT',
}

export enum IRemovedInstrumentStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export type EWeeksBetweenInvestments = 1 | 4 | 12;

export interface IDraftPortfolioOut {
  draftPortfolioId: number,
  name: string,
  portfolioType: 'PORTFOLIO' | 'STRATEGY',
  currency: ECurrencyCode,
  riskScore: number,
  strategyRebalance: boolean,
  initialDeposit: number,
  weeksBetweenInvestments: EWeeksBetweenInvestments,
  totalYearsToInvest: number,
  messageForClient: string,
  zoojMessage: string,
  autoExpiryTime: string,
  collaborator: IClientCollaboratorOut,
  modelPortfolioInstruments: IInvestmentModelInstrument[],
  dip: boolean,
  efektika: boolean,
  regularDeposit: number,
  investmentGoal: EInvestmentGoal,
  expectedValueInTargetYear?: number,
  aml?: boolean,
  canAcceptDraft?: boolean,
  collaboratorPhoto?: string | null,
  frameworkContractId: number;
  feeConfigId: number;
}

export interface IPortfolioDetailSummary {
  balance: number;
  sumOfDeposits: number;
  sumOfWithdrawals: number;
  actualYield: number;
}

export interface IPortfolioDetailTabSummary {
  creationDate?: string;
  totalYearsToInvest?: number;
  initialDeposit?: number;
  variableSymbol?: string;
  regularDeposit?: number;
  depositFrequency?: string;
  riskScore: number | null;
  loading: boolean;
}

export interface IPortfolioDetailTabTransactionHistory {
  transactions?: ITransaction[];
  loading: boolean;
}

export enum EFeeType {
    BASIC = 'BASIC',
    EXPRESS = 'EXPRESS',
    PROPORTIONAL = 'PROPORTIONAL',
    INITIAL_EXPRESS = 'INITIAL_EXPRESS',
}
export type PortfolioInstrumentRatioKeyType = number | 'cash';

export interface IEntryFeeInfo {
  totalEntryFee: number,
  entryFeeAlreadyPaid: number,
  vatAlreadyPaid: number,
  entryFeeAlreadyPaidWithVat: number,
  unpaidAmountWithVat: number,
  withVat: boolean,
  totalTargetRegularAmount: number,
  totalRegularAmount: number,
  remainingAmountToReachTargetActual: number,
}

export interface IRegularEntryFeeConfig {
  entryFeeType: EFeeType,
  entryDiscountRegular: number,
  entryFee: number,
};

export interface IPortfolioFeeConfigs {
  entryFeeInfo?: IEntryFeeInfo,
  regularEntryFeeConfig: IRegularEntryFeeConfig,
  oneTimeEntryFeeConfig: {
    entryFee: number,
  }
  entryFeeConfig?: {
    entryFeeType: EFeeType,
    entryDiscountRegular: number,
    entryFee: number,
  },
  exitFeeConfig: {
    exitFee: number,
  },
  performanceFeeConfig: {
    performanceFee: number,
  },
  mngFeeConfig: {
    mngFee: number,
  },
  variableServiceFeeConfig: {
    variableServiceFee?: number,
  },
  opfFeeImplementation: boolean,
  hasVat: boolean,
}

export interface IInstrumentOverview {
  isin: string;
  name: string;
  officialName: string;
  description: string;
  value: number;
  webUrl: string;
  weightActual?: number;
  weightIntended?: number;
  iconUrl: string | null;
  isCash: boolean;
  instrumentType?: string;
  yield1Y?: number;
  currencyCode?: ECurrencyCode;
  rebalancedMonthly?: boolean;
  willBeRemoved?: boolean;
  preventBottomBorder?: boolean;
  instrumentId?: number;
  totalYieldCzk?: number;
  totalYieldPct?: number;
  rawInstrument?: IInstrumentDetailFullOut;
}

export interface IInstrumentExpandableItemContext {
  $implicit: IInstrumentOverview;
  index: number;
}

export interface IModelPortfolioOut {
  portfolioId: number,
  name: string,
  portfolioType: 'PORTFOLIO' | 'STRATEGY',
  modelPortfolioInstruments: IInvestmentModelInstrument[],
  portfolioChangeAffectsFees: boolean,
  dip: boolean,
  image: {
    exists: boolean;
    url?: string;
  }
}

export enum EInvestmentGoal {
  ANNUITY_IN_RETIREMENT = 'ANNUITY_IN_RETIREMENT',
  FOR_KIDS = 'FOR_KIDS',
  FOR_HOUSING = 'FOR_HOUSING',
  CREATION_OF_RESERVE = 'CREATION_OF_RESERVE',
  OWN_TARGET = 'OWN_TARGET',
  WITHOUT_TARGET = 'WITHOUT_TARGET'
}

const investmentGoalTranslatePath = 'newInvestment.goal.options.';
export const investmentGoalTranslateMap: Record<EInvestmentGoal, { label: string; icon: string; }> = {
  [EInvestmentGoal.ANNUITY_IN_RETIREMENT]: {label: investmentGoalTranslatePath + 'rentTitle', icon: 'coins-add-plus.svg' },
  [EInvestmentGoal.FOR_KIDS]: {label: investmentGoalTranslatePath + 'childrenTitle', icon: 'baby.svg'},
  [EInvestmentGoal.FOR_HOUSING]: {label: investmentGoalTranslatePath + 'housingTitle', icon: 'home.svg'},
  [EInvestmentGoal.CREATION_OF_RESERVE]: {label: investmentGoalTranslatePath + 'setAsideTitle', icon: 'wallet.svg'},
  [EInvestmentGoal.WITHOUT_TARGET]: {label: investmentGoalTranslatePath + 'noGoalTitle', icon: 'question-mark-dashed-circle.svg'},
  [EInvestmentGoal.OWN_TARGET]: {label: investmentGoalTranslatePath + 'ownTargetTitle', icon: 'question-mark-dashed-circle.svg'},
}

export interface IPortfolioInstrumentRatios {
  portfolioId: number,
  totalAmount: number,
  currencyCode: string,
  instruments: IPortfolioInstrumentRatio[],
}

export type PortfolioCompositionTabTransMap = Record<EPortfolioCompositionTab, { key: EPortfolioCompositionTab, translation: string }>;

export interface IPortfolioInstrumentRatio {
  instrument: IInstrumentDetailFullOut,
  amount: number;
  amountRatio: number;
  modelRatio: number;
  realizedYieldCzk: number;
  realizedYieldPct: number;
  unrealizedYieldCzk: number;
  unrealizedYieldPct: number;
  totalYieldCzk: number;
  totalYieldPct: number;
}

export enum IPriceMeasurement {
  TWR = 'TWR',
  VALUE = 'VALUE',
}

export interface IPortfolioDetailChartParams {
  currencyCode?: ECurrencyCode,
  fromDate?: dayjs.Dayjs,
  toDate?: dayjs.Dayjs,
  size?: number,
  priceMeasure?: IPriceMeasurement,
}
export enum EPortfolioCompositionTab {
  ASSET_CATEGORY = 'assetCategory',
  REGION = 'region',
  CURRENCY = 'currency',
}

export interface IEntryFeeCalculationIn {
  frameworkContractId?: number;
  feeConfigId?: number;
  riskScore: number;
  instruments: Record<string, number>;
  initialDeposit: number;
  regularDeposit: number;
  totalYearsToInvest: number;
  weeksBetweenInvestments: number;
}

export interface IModelPortfolioByRiskScore {
  riskScore: number;
  name: string;
  instruments: IInvestmentModelInstrument[];
  description?: string;
}

export interface IEntryFeeCalcInput {
  changes: IClientPortfolioEntryFeeCalcInput;
}

export interface IClientPortfolioEntryFeeCalcInput {
  name: string;
  oneTimeDeposit: number;
  regularDeposit: number;
  timeOfInvestment: number;
  rebalancing: boolean;
  instrumentsWeights: Map<string, number> | Record<string, number>;
  depositRegularity: number;
  frameworkContractId: number;
  riskProfile: number;
  feeConfigId?: number; // drafts in fee calc
}

export interface IEntryFeeCalculationWithAllRatiosOut {
  feeRatio: number;
  entryRegularFeeIncludingVat: number;
  entryRegularFeeVat: number;
  entryRegularRatio: number;
  discountRegularDeposit: number;
  entryOneTimeRatio: number;
  entryOneTimeFeeIncludingVat: number;
  entryOneTimeFeeVat: number;
  mngFeeRatio: number;
  exitFeeRatio: number;
  performanceFeeRatio: number;
  serviceFeeRatio: number;
  entryFeeType: EFeeType,
  exitFeeRatioYear1: number,
  exitFeeRatioYear2: number,
  exitFeeRatioYear3: number,
  exitFeeRatioYear4: number,
  exitFeeRatioYear5: number,
}

export interface IModelPortfolioChartInput {
  depositRegularity: number;
  oneTimeDeposit: number;
  timeOfInvestment: number;
  regularDeposit: number;
  riskProfile: number;
  frameworkContractId: number;
  name?: string;
  feeConfigId?: number; // drafts in fee calc
}

export interface IModelStrategyChartInput {
  weeksBetweenInvestments: number,
  yearsToInvest: number,
  initialDeposit: number,
  regularDeposit: number,
  isinToWeightMap: Record<string, number>,
}

export interface IBackcastingChartInput {
  isinToWeightMap: Record<string, number>,
  graphSize: 'SMALL' | 'MEDIUM' | 'LARGE',
  periodInMonths?: number,
}
export type PaymentType = Record<
  'REGULAR' | 'ONETIME',
  {
    desc: string;
    explain: string;
    paymentInfo: string;
    code: string;
  }
>;

export interface IBlobProperties { imageProps: IImageUploadProps; photo: Blob }
export interface ICachedInstrumentWeight {
  weight: number;
  remember: boolean;
  isRebalancedMonthly?: boolean;
  maxLimitedInstrument?: number;
}
export interface INewInvestmentProgress {
  goal: EInvestmentGoal | null;
  investmentType: EInvestmentType | null;
  name: string | null;
  coverPhoto: IBlobProperties | null;
  investmentPeriodInput: number | null;
  regularInvestmentFrequency: string | null;
  initialInvestment: number | null;
  regularInvestment: number | null;
  riskScore: IRiskScoreSettings | null;
  frameworkContractId: number | null;
  isDip: boolean | null;
  selectedInstruments: IInstrumentOverview[];
  instrumentsWeights: Record<string, ICachedInstrumentWeight>;
  rebalancing: boolean | null;
  modelStrategyId: number | null;
  newInvestmentId: null | number;
  modelStrategyFlowType: EThematicStrategyFlow | null;
  firstInvestmentMode: boolean;
  photoUploadError: boolean;
}

export interface IEditInvestmentProgress {
  name: string;
  investmentType: EInvestmentType;
  coverPhoto: IBlobProperties | string | null;
  investmentPeriodInput: number;
  regularInvestmentFrequency: string;
  initialInvestment: number;
  regularInvestment: number;
  riskScore: IRiskScoreSettings | null;
  isDip: boolean;
  selectedInstruments: IInstrumentOverview[];
  instrumentsWeights: Record<string, ICachedInstrumentWeight>;
  rebalancing: boolean;
  editInvestmentId: number;
  frameworkContractId: number;
  originalInstrumentsWeights?: Record<string, ICachedInstrumentWeight>;
  photoUploadError: boolean;
  photoDeleteError: boolean;
}

export interface IInvestmentSettingsFormSource {
  name: string;
  coverPhoto: IBlobProperties | string | null;
  investmentPeriod: number;
  initialInvestment: number;
  regularInvestment: number;
  regularInvestmentFrequency: string;
  riskScore: IRiskScoreSettings;
  riskScoreSliderValue: number;
  frameworkContractId?: number;
  isDip: boolean;
}

export interface IRiskScoreSettings { riskScore: number; sliderValue: number };

export interface INewPortfolioInput {
  name: string;
  initialDeposit: number;
  regularDeposit: number;
  totalYearsToInvest: number;
  riskScore: number;
  weeksBetweenInvestments: number;
  frameworkContractId: number;
  investmentGoal: EInvestmentGoal;
  currencyCode: ECurrencyCode;
  coverPhoto?: IBlobProperties;
}

export interface INewStrategyInput {
  name: string,
  instruments: Record<string, number>,
  investmentFrequency: 'ONE_TIME' | 'CONTINUOUS',
  initialDeposit: number,
  regularDeposit: number,
  weeksBetweenInvestments: number,
  totalYearsToInvest: number,
  rebalance: boolean,
  frameworkContractId: number,
  investmentGoal: EInvestmentGoal;
  currencyCode: ECurrencyCode;
  coverPhoto?: IBlobProperties;
  modelStrategyId?: number;
}

export interface IInvestmentForm {
  name: FormControl<string>;
  coverPhoto: FormControl<IBlobProperties | null>;
  investmentPeriodSlider: FormControl<number>;
  investmentPeriodInput: FormControl<number>;
  initialInvestment: FormControl<number>;
  regularInvestment: FormControl<number>;
  regularInvestmentFrequency: FormControl<string>;
  riskScore: FormControl<IRiskScoreSettings>;
  riskScoreSliderValue: FormControl<number>;
  frameworkContractId: FormControl<number>;
  isDip: FormControl<boolean>;
  developmentModel: FormControl<'expected' | 'historical'>;
}

export interface IInvestmentFormValue {
  name: string;
  coverPhoto: IBlobProperties | string | null;
  investmentPeriodSlider: number;
  investmentPeriodInput: number;
  initialInvestment: number;
  regularInvestment: number;
  regularInvestmentFrequency: string;
  riskScore: IRiskScoreSettings;
  riskScoreSliderValue: number;
  frameworkContractId: number;
  isDip: boolean;
  developmentModel: 'expected' | 'historical';
}

export interface IInstrumentWeightForm {
  instrument: FormControl<IInstrumentOverview>;
  weight: FormControl<number>;
}

export interface IInvestmentInstrumentsWeightsForm {
  rebalancing: FormControl<boolean>;
  instruments: FormArray<FormGroup<IInstrumentWeightForm>>;
}

export interface IDipSetting {
  dipInitialValue: boolean;
  showDipSwitch: boolean;
  disableDipSwitch: boolean;
  showContractsSelect: boolean;
  frameworkContractId: number;
  disableOnEdit: boolean;
}

export interface IInstrumentsFilter {
  classes: EInstrumentCategory[];
  targets: EInstrumentTarget[];
  regions: string[];
  appliedSearch: string | null,
  filtersCount: number | null; // all - null
}

export enum ERiskSort {
  LOW_RISK = 'low-risk',
  HIGH_RISK = 'high-risk',
}

export interface IInstrumentsFilterSortSearchSetting {
  filters: IInstrumentsFilter;
  sort: ERiskSort;
  search: string;
}

export enum EThematicStrategyType {
  THEMATIC_STRATEGY = 'THEMATIC_STRATEGY',
  MODEL_PORTFOLIO = 'MODEL_PORTFOLIO',
  OKS = 'OKS',
}

export interface IApiThematicStrategy {
  modelStrategyId: number,
  type: EThematicStrategyType,
  name: string,
  yield1Y?: number,
  description?: string,
  shortDescription?: string,
  photoSmallUrl?: string,
  photoLargeUrl?: string,
  iconUrl?: string;
  imageUrl?: string;
  uiOrder?: number,
  efektika?: boolean,
  instruments?: IInvestmentModelInstrument[],
}

export interface ICardViewThematicStrategy {
  modelStrategyId: number,
  name: string,
  yield1Y?: number,
  description?: string,
  uiOrder?: number,
  photoSmallUrl?: string,
  photoLargeUrl?: string,
  type?: EThematicStrategyType,
}

export enum EInvestmentDetailMenuAction {
  EDIT = 1, ADD_MONEY, WITHDRAW_MONEY, DOWNLOAD_EMPLOYER_PAYMENT_INFO, DELETE
}

export interface IApiModelStrategy {
  modelStrategyId: number,
  name: string,
  description: string,
  imageUrl: string,
  iconUrl: string,
  translate?: true // FE flag only
  uiOrder?: number | null,
  type: ModelStrategyTypes,
  efektika?: boolean,
}

export enum ModelStrategyTypes {
  THEMATIC_STRATEGY = 'THEMATIC_STRATEGY',
  MODEL_PORTFOLIO = 'MODEL_PORTFOLIO',
  OKS = 'OKS',
}

export interface IModelStrategyInstrumentOut {
  instrument: IInstrumentDetailFullOut,
  weight: number,
}

export interface IModelProductWithInstrumentsOut extends IApiModelStrategy {
  instruments: IModelStrategyInstrumentOut[];
}

export enum EWEightsPageModelStrategyBackOrigin {
  GOALS_PAGE = 'goals',
  THEMATIC_STRATEGIES_LIST_PAGE = 'thematic-strategies',
}

export interface IClientRequestInput {
  portfolioId: number,
}

export interface IPortfolioDepositOut {
  accountNumber: string,
  bankCode: string,
  iban: string,
  currency: 'CZK' | 'EUR',
  regularDeposit: number,
  variableSymbol: string,
  entryFeeInfo?: IEntryFeeInfoOut,
  specificSymbols: ISpecificSymbol[],
}

interface ISpecificSymbol {
  type: ESpecificSymbolTypes,
  symbol: string,
}

export enum ESpecificSymbolTypes {
  INITIAL = 'INITIAL',
  REGULAR = 'REGULAR',
  EMPLOYER_INITIAL = 'EMPLOYER_INITIAL',
  EMPLOYER_REGULAR = 'EMPLOYER_REGULAR',
  REFUND_ENTRY = 'REFUND_ENTRY',
  REFUND_MANAGEMENT = 'REFUND_MANAGEMENT',
}

interface IEntryFeeInfoOut {
  entryFeeAlreadyPaid: null | number,
  totalEntryFee: null | number,
  unpaidAmountWithVat: null | number,
  vatAlreadyPaid: null | number,
  entryFeeAlreadyPaidWithVat: null | number,
}

// todo - shouldnt flow-routes related routes be in some different file/folder?
export enum EThematicStrategyFlow {
  // Dasboard → Select TS → Goals → Weights (+Optional instruments) → Settings
  QUICK_ROTATION_DASHBOARD = 'quick-rotation-dashboard',
  // My investments → Select TS → Goals → Weights (+Optional instruments) → Settings
  QUICK_ROTATION_MY_INVESTMENTS = 'quick-rotation-my-investments',
  // Dasboard → View all TSs → Select TS → Goals → Weights (+Optional instruments) → Settings
  QUICK_TS_LIST = 'quick-ts-list',
  // Thematic strategy landing page → Select TS → Goals → Weights (+Optional instruments) → Settings
  TS_LIST_PRODUCT_PAGE = 'ts-list-product-page',
  // Dashboard → New investment → Goal → Type → Select TS → All TSs → Select TS → Weights (+Optional instruments) → Settings
  STANDARD = 'standard',
}

export interface IUpdatePortfolioV2Input {
  newPortfolioName: string,
  initialDeposit: number,
  regularDeposit: number,
  weeksBetweenInvestments: EInvestmentFrequency,
  totalYearsToInvest: number,
  updatedCoverPhoto?: IBlobProperties;
  deletePhoto?: boolean;
}

export interface IUpdateStrategyInput {
  instruments: Record<string, number>,
  rebalance: boolean,
  newPortfolioName: string,
  initialDeposit: number,
  regularDeposit: number,
  weeksBetweenInvestments: EInvestmentFrequency,
  totalYearsToInvest: number,
  updatedCoverPhoto?: IBlobProperties;
  deletePhoto?: boolean;
}

export interface IPortfolioDraftsCheckOptions {
  mpis: IInvestmentModelInstrument[], allowedCats?: EInstrumentCategory[], draftRiskScore?: number, maxUserRiskScore?: number | null
}

export enum EDraftPortfolioStatus {
  SENT = 'SENT',
  EXPIRED = 'EXPIRED',
  ACCEPTED = 'ACCEPTED',
  REFUSED = 'REFUSED'
}

export interface IDraftStatusResponse {
  draftPortfolioId: number,
  draftPortfolioStatus: EDraftPortfolioStatus,
  portfolioId: number | null
}

export interface IAcceptDraftResponse {
  portfolioId: number
}

export interface IUpdateEfektikaStrategyInput {
  rebalance: boolean;
  newEfektikaName: string;
  updatedCoverPhoto?: IBlobProperties; // not currently in v1.0
  deletePhoto?: boolean; // not currently in v1.0
}

export enum EClientAccountAdditionNewIn {
  PM_TO_EO_CONVERSION = 'PM_TO_EO_CONVERSION',
  INVEST_QUEST_INSTANT_CATEGORY = 'INVEST_QUEST_INSTANT_CATEGORY',
  OPF_TRANSFER = 'OPF_TRANSFER',
}

export enum EDisabledAdditionReasons {
  WAITING_FOR_SIGNATURE = 'WAITING_FOR_SIGNATURE',
  WAITING_FOR_PROCESSING = 'WAITING_FOR_PROCESSING',
  NOT_EXECUTION_ONLY_CATEGORIES = 'NOT_EXECUTION_ONLY_CATEGORIES',
  NO_PORTFOLIO_TO_TRANSFER = 'NO_PORTFOLIO_TO_TRANSFER',
  EXECUTION_ONLY_NO_ADDED_VALUE = 'EXECUTION_ONLY_NO_ADDED_VALUE',
  NOT_ENGLISH_SPEAKER = 'NOT_ENGLISH_SPEAKER',
  NOT_ALLOWED_BY_COLLABORATOR  = 'NOT_ALLOWED_BY_COLLABORATOR',
  NO_FRAMEWORK_CONTRACT_TO_TRANSFER = 'NO_FRAMEWORK_CONTRACT_TO_TRANSFER',
}

export interface IClientAccountAdditionOut {
  type: EClientAccountAdditionNewIn,
  reasonWhyNotList: EDisabledAdditionReasons[],
}

export interface IEmailVerificationOut {
  reasonWhyNotList: EEmailConfirmErrors,
  type: EEmailConfirmTypes,
}

export enum EEmailConfirmTypes {
  DIP_REGULAR_WITHDRAWAL = 'DIP_REGULAR_WITHDRAWAL',
  REGULAR_WITHDRAWAL_DRAFT_TO_DIP = 'REGULAR_WITHDRAWAL_DRAFT_TO_DIP',
}

export enum EEmailConfirmErrors {
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALIDATED = 'TOKEN_INVALIDATED',
  TOKEN_ALREADY_CONFIRMED = 'TOKEN_ALREADY_CONFIRMED',
}

export interface IRegularWithdrawal {
  regularWithdrawalId: number;
  portfolioId: number;
  portfolioName: string;
  bankAccount: {
    bankAccountId: number;
    accountNumber: string;
    bankCode: string;
    logoUrl: string;
  };
  mode: ERegularWithdrawalMode;
  value: number;
  frequency: ERegularWithdrawalFrequency;
  status?: ERegularWithdrawalStatus;
  validFrom: string;
  validTo?: string;
  nextExecutionDate: string | null;
  sumOfWithdrawals: number;
  image: IMinioImage;
  portfolioType: EManagementType;
}

export enum ERegularWithdrawalMode {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

export enum ERegularWithdrawalFrequency {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

export enum ERegularWithdrawalStatus {
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
  ERROR = 'ERROR',
  INVALID_PORTFOLIO_STATE = 'INVALID_PORTFOLIO_STATE',
}

export interface IRegularWithdrawalDraftsResponse {
  aml: boolean;
  regularWithdrawalDrafts: IRegularWithdrawalDraft[];
}

export interface IRegularWithdrawalDraft {
  regularWithdrawalDraftId: number;
  portfolioId: number;
  dip: boolean;
  portfolioName: string;
  bankAccountId: number;
  accountNumber: string;
  bankCode: string;
  mode: ERegularWithdrawalMode;
  value: number;
  frequency: ERegularWithdrawalFrequency;
  status: ERegularWithdrawalDraftStatus;
  collaborator: IClientCollaboratorOut;
  message: string;
  expiredAt: string;
  validTo: string;
  logoUrl: string;
  image: IMinioImage;
  portfolioManagementType: EManagementType;
  investmentGoalType: EInvestmentGoal;
  efektika: boolean;
  // these two added in response processing if relevant
  aml?: boolean;
  collaboratorPhoto?: boolean;
}

export enum ERegularWithdrawalDraftStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PENDING_DIP_APPROVAL = 'PENDING_DIP_APPROVAL',
  CREATED = 'CREATED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

export interface IRegularWithdrawalAcceptSettings extends IRegularWithdrawalCreateSettings {
  regularWithdrawalDraftId: number;
}

export interface IRegularWithdrawalRefuseSettings {
  regularWithdrawalDraftId: number;
}

export interface IRegularWithdrawalCreateSettings {
  investmentId: number;
  managementType: EManagementType;
  investmentName: string;
  bankAccount: string;
  frequency: ERegularWithdrawalFrequency;
  validTo: string | null;
  amount: number;
  isDip: boolean;
  photoUrl: string | null;
  isEfektika: boolean;
}

export interface IRegularWithdrawalAcceptConfirm extends IRegularWithdrawalCreateConfirm {
  regularWithdrawalDraftId?: number;
}

export interface IRegularWithdrawalCreateConfirm {
  investmentId: number;
  isDip: boolean;
  photoUrl: string | null;
  amount: number;
  investmentName: string;
  isEfektika: boolean;
}

export interface IRegularWithdrawalLimits {
  maxAllowedWithdrawal: number;
  minAllowedWithdrawal: number;
  currency: string;
}

export enum EWithdrawalPortfolioType {
  WITHDRAWAL = 'WITHDRAWAL',
  REGULAR_WITHDRAWAL = 'REGULAR_WITHDRAWAL',
}

export enum ERegularWithdrawalValidity {
  UNLIMITED = 'UNLIMITED',
  LIMITED = 'LIMITED',
}
export interface INewRegularWithdrawalInput {
  portfolioId: number;
  bankAccountId: number;
  mode: ERegularWithdrawalMode;
  value: number;
  frequency: ERegularWithdrawalFrequency;
  validTo: string | null; // eg. 2025-12-03
}

// todo - ❓'draft' seems unused ❔
export type InstrumentListModes = 'new' | 'editing' | 'draft' | 'public-view' | 'product-page';

export enum EProductLandingType {
  THEMATIC_STRATEGY = 'thematicStrategyLanding',
  CUSTOM_STRATEGY = 'customStrategyLanding',
  DIP = 'dipLanding',
}

export interface IWithdrawableAmount {
  balance: number;
  withdrawable: number;
  liquidationThreshold: number;
  exitFeeRatio: number;
  pendingWithdrawalsAmount: number;
  pendingDipApprovalAmount: number;
  containsWithdrawAllRequest: boolean;
  liquidationInProgress: boolean;
  currency: string;
}

